import { useNavigate } from 'react-router-dom'

import { Box, Button, Tag, TagColor, Text } from '@factorialco/deprecated-design-system'
import { startCase } from 'lodash'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import {
  ResultConcepts,
  ResultConceptsLabelsType,
  resultConceptsLabels,
} from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import type Payment from 'modules/payroll_bundle/store/models/Payment'

interface Props {
  payment: Payment
  lastPaymentId: number
  hasSinglePayment: boolean
  currency: string
}

const PaymentListItem = ({ payment, lastPaymentId, hasSinglePayment, currency }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { policyId, currentPeriod } = usePolicyPeriods()
  const { paymentDispatch } = usePayrollPayments()
  const author = payment.author
  const type: string = payment.attributes.concept
  const createdAtText = moment(payment.attributes.created_at).format('MMM D')
  const titleText = resultConceptsLabels()[type as ResultConceptsLabelsType]
  const periodId = currentPeriod?.id

  // These are guaranteed to be present through the render hierarchy.
  const pathParams = {
    policyId: policyId!, // eslint-disable-line no-type-assertion/no-type-assertion
    periodId: periodId!, // eslint-disable-line no-type-assertion/no-type-assertion
  }
  const COLOR_BY_STATUS = {
    pending: TagColor.champagne,
    paid: TagColor.lime,
  } as const

  let paymentPath: string
  if (payment.concept === ResultConcepts.regular_payroll) {
    paymentPath = navigation.payrollBundle.policy.period.cycles.payments.fullPath(pathParams)
  } else {
    paymentPath = navigation.payrollBundle.policy.period.offcycles.payments.fullPath(pathParams)
  }

  const handlePaymentDetailsDisplay = (value: boolean) => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        showPaymentDetailsSidepanel: value,
        paymentDetailsToShow: payment,
      },
    })
    navigate(paymentPath)
  }

  return (
    <Box
      key={payment.attributes.id}
      height="s64"
      padding="s16"
      border={{
        bottom:
          hasSinglePayment || lastPaymentId === payment.attributes.id
            ? undefined
            : { width: 's1', style: 'solid', color: 'grey400' },
      }}
      justifyContent="center"
      cursor="pointer"
      onClick={() => navigate(paymentPath)}
      aria-label="payment item"
    >
      <Box alignItems="center" justifyContent="spaceBetween" width="full" flexDirection="row">
        <Box minWidth="fit-content" width="s200">
          <Text.Normal size="300" weight="medium">
            {titleText}
          </Text.Normal>
        </Box>
        <Box alignItems="center" justifyContent="spaceBetween" width="full" flexDirection="row">
          <Box justifyContent="flexStart">
            <Text.Normal size="200" weight="medium">
              {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.amount_for_employees', {
                paymentAmount: formatAmount(payment.paymentsTotalAmount, currency),
                employeeCount: payment.totalEmployees,
              })}
            </Text.Normal>
          </Box>
          <Box justifyContent="flexStart">
            {author && (
              <Text.Normal size="300">
                {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.created_on', {
                  authorFirstName: author.attributes.first_name?.trim() || '',
                  created_date: createdAtText,
                })}
              </Text.Normal>
            )}
          </Box>
          <Box flexDirection="row" alignItems="center" gap="s40">
            <Box justifyContent="flexEnd" flexDirection="row" width="s120">
              <Box alignSelf="center">
                <Tag
                  type={COLOR_BY_STATUS[payment.attributes.status as keyof typeof COLOR_BY_STATUS]}
                >
                  {startCase(payment.attributes.status)}
                </Tag>
              </Box>
            </Box>
            <Box width="fit-content">
              <Button
                onClick={() => handlePaymentDetailsDisplay(true)}
                type="secondary"
                iconName="arrow-right"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PaymentListItem
